/* Sale cơ sở: khách Telesales hẹn đến chi nhánh — check-in, nhận tư vấn, ghi kết quả (lưu trên máy) */
import React, { useMemo, useState } from 'react';
import { Alert, Button, Input, InputNumber, Modal, Radio, Segmented, Select, Table, Tag, Tooltip, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { CalendarCheck2, CalendarClock, CircleDollarSign, UserCheck, UserX } from 'lucide-react';
import type { ChannelCustomerItem } from '../../../api/ChannelCustomer';
import type { CustomerTagItem } from '../../../api/CustomerTag';
import { BRANCHES } from '../dashboard/data';
import { Card, Kpi, money } from '../dashboard/ui';
import {
  MIN, OUTCOMES, dur, findTag, maskPhone, shortTag, tagOf, type LeadAppt, type OutcomeKind,
} from './logic';
import { useLeadStore } from './store';

type Range = 'today' | 'tomorrow' | 'next7';
const RANGE_OPTS = [{ value: 'today', label: 'Hôm nay' }, { value: 'tomorrow', label: 'Ngày mai' }, { value: 'next7', label: '7 ngày tới' }];
/** Quá giờ hẹn N phút chưa check-in thì cho đánh dấu Không đến */
const NOSHOW_AFTER_MIN = 15;

interface Row { id: number; rec: ChannelCustomerItem; a: LeadAppt }

const ST: Record<string, { label: string; color: string }> = {
  booked: { label: 'Chưa xác nhận', color: 'gold' },
  confirmed: { label: 'Đã xác nhận', color: 'blue' },
  arrived: { label: 'Đã đến', color: 'cyan' },
  consulting: { label: 'Đang tư vấn', color: 'processing' },
  noshow: { label: 'Không đến', color: 'red' },
};

export default function BranchBoard({ data, tags, me, now, onOpen }: {
  data: ChannelCustomerItem[];
  tags: CustomerTagItem[];
  me: string;
  now: number;
  onOpen: (r: ChannelCustomerItem) => void;
}) {
  const byId = useLeadStore((s) => s.byId);
  const { patch, addTouch, setApptStatus, setOutcome } = useLeadStore();
  const [range, setRange] = useState<Range>('today');
  const [branch, setBranch] = useState(BRANCHES[0].name);
  const [quick, setQuick] = useState<string | null>(null);
  const [result, setResult] = useState<Row | null>(null);

  const rows = useMemo<Row[]>(() => {
    const d0 = dayjs(now).startOf('day');
    const [from, to] = range === 'today' ? [d0, d0.add(1, 'day')] : range === 'tomorrow' ? [d0.add(1, 'day'), d0.add(2, 'day')] : [d0, d0.add(7, 'day')];
    return Object.entries(byId).flatMap(([k, l]) => {
      const id = Number(k);
      const rec = data.find((r) => r.id === id)
        ?? ({ id, display_name: '', fullname: null, phone: null, customer_code: null } as unknown as ChannelCustomerItem);
      return l.appts
        .filter((a) => a.branch === branch && a.status !== 'cancelled' && a.at >= from.valueOf() && a.at < to.valueOf())
        .map((a) => ({ id, a, rec: rec.display_name ? rec : { ...rec, display_name: a.cust?.name || `KH-${id}`, phone: a.cust?.phone ?? null, customer_code: a.cust?.code ?? null } }));
    }).sort((x, y) => x.a.at - y.a.at);
  }, [byId, data, branch, range, now]);

  const late = (a: LeadAppt) => (a.status === 'booked' || a.status === 'confirmed') && a.at < now;
  const stKey = (a: LeadAppt) => (a.status === 'arrived' && a.consultant ? 'consulting' : a.status);
  const k = {
    total: rows.length,
    arrived: rows.filter((r) => r.a.status === 'arrived' || r.a.status === 'done').length,
    waiting: rows.filter((r) => r.a.status === 'booked' || r.a.status === 'confirmed').length,
    noshow: rows.filter((r) => r.a.status === 'noshow').length,
    won: rows.filter((r) => r.a.outcome && (r.a.outcome.kind === 'won' || r.a.outcome.kind === 'deposit')),
  };
  const shown = rows.filter((r) => !quick
    || (quick === 'arrived' && (r.a.status === 'arrived' || r.a.status === 'done'))
    || (quick === 'waiting' && (r.a.status === 'booked' || r.a.status === 'confirmed'))
    || (quick === 'noshow' && r.a.status === 'noshow')
    || (quick === 'won' && k.won.includes(r)));

  /** Đổi mã S trên máy và ghi lịch sử chạm */
  const moveStage = (r: Row, code: string) => {
    const l = useLeadStore.getState().byId[r.id];
    const from = tagOf(r.rec, l, tags);
    const to = findTag(tags, code);
    if (!to || to.id === from?.id) return;
    patch(r.id, { tagId: to.id });
    addTouch(r.id, { kind: 'stage', text: `Mã S ${shortTag(from?.name) || 'chưa có'} → ${shortTag(to.name)}`, by: me });
  };

  const checkIn = (r: Row) => {
    setApptStatus(r.id, r.a.id, 'arrived', me);
    moveStage(r, 'S6');
    message.success(`${r.rec.display_name} đã check-in`);
  };
  const noShow = (r: Row) => {
    setApptStatus(r.id, r.a.id, 'noshow', me);
    moveStage(r, 'S5.1');
    // Trả về hàng đợi Telesales: gọi lại ngay để đặt lịch mới
    patch(r.id, { callback: Date.now() });
    message.info('Đã trả khách về Telesales gọi lại');
  };
  const takeOver = (r: Row) => {
    setApptStatus(r.id, r.a.id, 'arrived', me, { consultant: me });
  };

  const columns: ColumnsType<Row> = [
    {
      title: 'GIỜ HẸN', width: 110,
      render: (_, { a }) => (
        <div className="leading-tight">
          <div className="text-[15px] font-bold tabular-nums text-gray-800 dark:text-gray-100">{dayjs(a.at).format('HH:mm')}</div>
          {range !== 'today' && <div className="text-[12px] text-gray-400">{dayjs(a.at).format('DD/MM')}</div>}
          {late(a) && <div className="text-[12px] font-semibold text-red-500">Trễ {dur(now - a.at)}</div>}
        </div>
      ),
    },
    {
      title: 'KHÁCH', width: 200,
      render: (_, r) => (
        <button type="button" onClick={() => onOpen(r.rec)} className="min-w-0 text-left">
          <div className="truncate font-semibold text-[#3e91ab] hover:underline">{r.rec.display_name}</div>
          <div className="text-[12px] text-gray-400">{r.rec.customer_code || `KH-${r.id}`}{r.rec.phone ? ` · ${maskPhone(r.rec.phone)}` : ''}</div>
        </button>
      ),
    },
    {
      title: 'NHU CẦU', width: 240,
      render: (_, { a }) => (
        <div className="leading-tight">
          <div className="text-gray-700 dark:text-gray-300">{a.mang}{a.service ? ` · ${a.service}` : ''}</div>
          {a.note && <div className="mt-0.5 line-clamp-2 text-[12px] italic text-gray-500">“{a.note}”</div>}
        </div>
      ),
    },
    {
      title: 'TELESALES / TƯ VẤN', width: 170,
      render: (_, { a }) => (
        <div className="leading-tight">
          <div className="text-gray-700 dark:text-gray-300">{a.by}</div>
          <div className="text-[12px] text-gray-400">{a.consultant ? `Tư vấn: ${a.consultant}` : 'Chưa nhận tư vấn'}</div>
        </div>
      ),
    },
    {
      title: 'TRẠNG THÁI', width: 160,
      render: (_, { a }) => {
        if (a.outcome) {
          const o = OUTCOMES[a.outcome.kind];
          return (
            <div className="leading-tight">
              <Tag color={o.color} className="m-0">{o.label.split(' —')[0]}</Tag>
              {a.outcome.amount ? <div className="mt-1 text-[12px] font-semibold text-emerald-600">{money(a.outcome.amount)}</div> : null}
            </div>
          );
        }
        const s = ST[stKey(a)];
        return <Tag color={s.color} className="m-0">{s.label}</Tag>;
      },
    },
    {
      title: '', width: 200, fixed: 'right', align: 'right',
      render: (_, r) => {
        const { a } = r;
        if (a.status === 'booked' || a.status === 'confirmed') {
          if (!dayjs(a.at).isSame(now, 'day')) return <span className="text-[12px] text-gray-400">Chờ ngày hẹn</span>;
          const canNoShow = now > a.at + NOSHOW_AFTER_MIN * MIN;
          return (
            <div className="flex justify-end gap-1.5">
              <Button size="small" type="primary" icon={<UserCheck size={13} />} onClick={() => checkIn(r)} className="!bg-[#3e91ab]">Check-in</Button>
              <Tooltip title={canNoShow ? 'Trả khách về Telesales gọi lại' : `Chỉ đánh dấu sau giờ hẹn ${NOSHOW_AFTER_MIN} phút`}>
                <Button size="small" danger disabled={!canNoShow} icon={<UserX size={13} />} onClick={() => noShow(r)}>Không đến</Button>
              </Tooltip>
            </div>
          );
        }
        if (a.status === 'arrived') {
          return (
            <div className="flex justify-end gap-1.5">
              {!a.consultant && <Button size="small" onClick={() => takeOver(r)}>Nhận tư vấn</Button>}
              <Button size="small" type="primary" onClick={() => setResult(r)} className="!bg-[#3e91ab]">Ghi kết quả</Button>
            </div>
          );
        }
        return <Button size="small" type="link" onClick={() => onOpen(r.rec)}>Xem hồ sơ</Button>;
      },
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Select value={branch} onChange={setBranch} options={BRANCHES.map((b) => ({ value: b.name, label: b.name }))} className="w-[200px]" />
        <Segmented value={range} onChange={(v) => setRange(v as Range)} options={RANGE_OPTS} />
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
        <Kpi icon={<CalendarClock size={15} />} label="Lịch hẹn" value={k.total} onClick={() => setQuick(null)} active={!quick} />
        <Kpi icon={<UserCheck size={15} />} tone="green" label="Đã đến" value={k.arrived} sub={k.total ? `${Math.round((k.arrived / k.total) * 100)}% lịch hẹn` : undefined} onClick={() => setQuick('arrived')} active={quick === 'arrived'} />
        <Kpi icon={<CalendarCheck2 size={15} />} tone="amber" label="Chờ khách đến" value={k.waiting} sub={rows.filter((r) => late(r.a)).length ? `${rows.filter((r) => late(r.a)).length} khách đang trễ` : undefined} onClick={() => setQuick('waiting')} active={quick === 'waiting'} />
        <Kpi icon={<UserX size={15} />} tone="red" label="Không đến" value={k.noshow} sub="Đã trả về Telesales" onClick={() => setQuick('noshow')} active={quick === 'noshow'} />
        <Kpi icon={<CircleDollarSign size={15} />} tone="indigo" label="Chốt / cọc" value={k.won.length} sub={k.won.length ? `Doanh số ${money(k.won.reduce((sum, r) => sum + (r.a.outcome?.amount ?? 0), 0))}` : undefined} onClick={() => setQuick('won')} active={quick === 'won'} />
      </div>

      <Card title={`Khách hẹn đến · ${branch}`}>
        <Table
          rowKey={(r) => r.a.id}
          columns={columns}
          dataSource={shown}
          pagination={false}
          size="middle"
          scroll={{ x: 1080 }}
          locale={{ emptyText: <div className="py-6 text-gray-400">Không có lịch hẹn {RANGE_OPTS.find((o) => o.value === range)?.label.toLowerCase()} tại {branch}</div> }}
        />
      </Card>

      {result && <OutcomeModal row={result} onClose={() => setResult(null)} onSave={(kind, amount, note) => {
        setOutcome(result.id, result.a.id, { kind, amount, note, by: me, at: Date.now() });
        moveStage(result, OUTCOMES[kind].stage);
        if (kind === 'think') patch(result.id, { callback: dayjs(now).add(1, 'day').hour(10).minute(0).second(0).valueOf() });
        setResult(null);
        message.success(`Đã ghi kết quả: ${OUTCOMES[kind].label}`);
      }} />}
    </div>
  );
}

function OutcomeModal({ row, onClose, onSave }: { row: Row; onClose: () => void; onSave: (k: OutcomeKind, amount: number | undefined, note: string) => void }) {
  const [kind, setKind] = useState<OutcomeKind>();
  const [amount, setAmount] = useState<number | null>(null);
  const [note, setNote] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const needMoney = kind === 'won' || kind === 'deposit';
  const needNote = kind === 'no_need' || kind === 'medical' || kind === 'think';
  const submit = () => {
    if (!kind) return setErr('Chọn kết quả tư vấn.');
    if (needMoney && !amount) return setErr(kind === 'won' ? 'Nhập giá trị dịch vụ đã chốt.' : 'Nhập số tiền cọc.');
    if (needNote && !note.trim()) return setErr('Ghi lý do để Telesales / vận hành nắm.');
    onSave(kind, needMoney ? amount ?? undefined : undefined, note.trim());
  };
  return (
    <Modal open title={`Kết quả tư vấn · ${row.rec.display_name}`} okText="Lưu kết quả" cancelText="Huỷ" onOk={submit} onCancel={onClose} okButtonProps={{ className: '!bg-[#3e91ab]' }}>
      <div className="space-y-3 pt-1">
        <div className="text-[12.5px] text-gray-500">Lịch {dayjs(row.a.at).format('HH:mm DD/MM')} · {row.a.mang}{row.a.service ? ` · ${row.a.service}` : ''}</div>
        <Radio.Group value={kind} onChange={(e) => { setKind(e.target.value); setErr(null); }} className="flex flex-col gap-2">
          {(Object.keys(OUTCOMES) as OutcomeKind[]).map((o) => (
            <Radio key={o} value={o}>{OUTCOMES[o].label} <span className="text-[12px] text-gray-400">→ {OUTCOMES[o].stage}</span></Radio>
          ))}
        </Radio.Group>
        {needMoney && (
          <InputNumber<number>
            className="!w-full"
            min={0}
            step={500000}
            value={amount}
            onChange={setAmount}
            placeholder={kind === 'won' ? 'Giá trị dịch vụ (đ)' : 'Số tiền cọc (đ)'}
            formatter={(v) => (v ? Number(v).toLocaleString('vi-VN') : '')}
            parser={(v) => Number((v || '').replace(/\D/g, ''))}
          />
        )}
        <Input.TextArea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder={needNote ? 'Lý do (bắt buộc)' : 'Ghi chú (không bắt buộc)'} maxLength={300} />
        {kind === 'think' && <Alert type="info" showIcon message="Khách trở về hàng đợi Telesales, hẹn gọi lại 10:00 ngày mai." />}
        {err && <Alert type="error" showIcon message={err} />}
      </div>
    </Modal>
  );
}

